'use client'

import { chainValidations, makeFormStateWithModel } from 'formtastisch'
import { type FC, useMemo, useState } from 'react'
import { type IntlShape, useIntl } from 'react-intl'

import { SubscriptionTypeV1 } from '@redteclab/api/clients/bully'

import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Notification,
} from 'base-ui'

import { validateEmail, validateRequired } from '../../../base-form-handling'
import { SeoHeadTitleMetas } from '../../../seo'
import { useApiSwrNewsletterSubscribeUnsubscribeRequestMutation } from '../../api/useNewsletterSubscribeUnsubscribeRequestMutation'

import { NewsletterSubscribeUnsubscribeBenefitsBox } from './NewsletterSubscribeUnsubscribeBenefitsBox'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- neccessary for the form state
const makeNewsletterSubscribeUnsubscribeFormState = (intl: IntlShape) => {
  return makeFormStateWithModel({
    initialData: {
      email: '',
    },
    validations: {
      email: (value) => {
        return chainValidations(
          () =>
            validateRequired(
              value,
              intl.formatMessage({ id: 'form.error.input.missing' }),
            ),

          () =>
            validateEmail(
              value,
              intl.formatMessage({ id: 'error.email.invalid' }),
            ),
        )
      },
    },
  })
}

type NewsletterSubscribeUnsubscribeFormState = ReturnType<
  typeof makeNewsletterSubscribeUnsubscribeFormState
>

const handleSubmit = async ({
  formState,
  newsletterSubscribeUnsubscribeSwr,
  setHasError,
  setIsUnsubscribed,
}: {
  formState: NewsletterSubscribeUnsubscribeFormState
  newsletterSubscribeUnsubscribeSwr: ReturnType<
    typeof useApiSwrNewsletterSubscribeUnsubscribeRequestMutation
  >
  setHasError: (error: boolean) => void
  setIsUnsubscribed: (subscribed: boolean) => void
}): Promise<void> => {
  if (newsletterSubscribeUnsubscribeSwr.isMutating) {
    return
  }

  formState.validate()
  if (!formState.isValid()) {
    return
  }

  const { model } = formState

  try {
    await newsletterSubscribeUnsubscribeSwr.trigger({
      action: 'unsubscribe',
      emailAddress: model.email,
      subscriptionType: SubscriptionTypeV1.Newsletter,
    })
    setIsUnsubscribed(true)
    setHasError(false)
  } catch {
    setHasError(true)
    setIsUnsubscribed(false)
  }
}

export const NewsletterSubscribeUnsubscribePageContent: FC = () => {
  const intl = useIntl()

  const [hasError, setHasError] = useState(false)
  const [isUnsubscribed, setIsUnsubscribed] = useState(false)

  const formState = useMemo(
    () => makeNewsletterSubscribeUnsubscribeFormState(intl),
    [intl],
  ).use()

  const { model } = formState

  const newsletterSubscribeUnsubscribeSwr =
    useApiSwrNewsletterSubscribeUnsubscribeRequestMutation()

  return (
    <div
      className="bg-light-brand-low"
      data-qa-id="newsletter-subscribe-unsubscribe-page"
    >
      <SeoHeadTitleMetas
        title={intl.formatMessage({ id: 'newsletter.unsubscribe.headline' })}
      />
      <div className="mx-auto max-w-lg px-4 py-8">
        <h1 className="mb-4 text-3xl font-normal tablet:text-center">
          {intl.formatMessage({
            id: 'newsletter.unsubscribe.headline',
          })}
        </h1>
        {hasError ? (
          <Notification
            className="mb-6"
            data-qa-id="newsletter-subscribe-error-notification"
            severity="error"
          >
            {intl.formatMessage({ id: 'general.error' })}
          </Notification>
        ) : null}
        {isUnsubscribed ? (
          <Notification
            className="mb-6"
            data-qa-id="newsletter-subscribe-success-notification"
            severity="success"
          >
            {intl.formatMessage({
              id: 'account.newsletter.unsubscribe.notification.success',
            })}
          </Notification>
        ) : null}
        <h2 className="mb-7 text-l font-normal text-dark-primary-strong tablet:text-center">
          {intl.formatMessage({
            id: 'newsletter.unsubscribe.sorryMessage',
          })}
        </h2>
        <NewsletterSubscribeUnsubscribeBenefitsBox />
        <form
          className="mb-6 flex flex-col gap-5"
          data-qa-id="newsletter-subscribe-unsubscribe-form"
          onSubmit={(event): void => {
            event.preventDefault()
            void handleSubmit({
              formState,
              newsletterSubscribeUnsubscribeSwr,
              setHasError,
              setIsUnsubscribed,
            })
          }}
        >
          <FormControl
            error={!model.validator.isPropertyValid('email')}
            {...formState.makeInputProps('email', { validateOnBlur: true })}
            required
          >
            <FormLabel htmlFor="newsletter-subscribe-email-input">
              {intl.formatMessage({ id: 'general.label.email' })}
            </FormLabel>
            <Input
              id="newsletter-subscribe-email-input"
              slotProps={{
                input: {
                  'data-qa-id':
                    'newsletter-unsubscription-form-newsletterEmail',
                },
              }}
            />
            {model.validator.getFirstErrorFor('email') && (
              <FormHelperText data-qa-id="newsletterEmail-error-msg">
                {model.validator.getFirstErrorFor('email')}
              </FormHelperText>
            )}
          </FormControl>
          <Button
            className=""
            data-qa-id="newsletter-unsubscription-form-unsubscribe"
            type="submit"
          >
            {intl.formatMessage({ id: 'newsletter.unsubscribe.button' })}
          </Button>
        </form>
        <p className="mb-7 text-dark-primary-low">
          {intl.formatMessage({ id: 'general.disclaimer.mandatory.fields' })}
        </p>
        <p className="text-dark-primary-low">
          {intl.formatMessage({ id: 'newsletter.page.unsub.info' })}
        </p>
      </div>
    </div>
  )
}
